import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ColumnsType } from 'antd/lib/table';
import { RootState } from 'app/AppStore';
import { AddToEstimateButtonAndDialog } from 'components/AddToEstimate/AddToEstimateButtonAndDialog';
import QuantityModule from 'components/QuantityModule';
import { ReferenceUnavailableForOrder } from 'components/ReferenceUnavailableBox/ReferenceUnavailable';
import StockDisplay from 'components/StockInfo';
import { isBulkCertasOil } from 'components/StockInfo/catalogStockUtils';
import StopPropagation from 'components/StopPropagation/StopPropagation';
import {
  addOtherSectionReferenceRequest,
  getBasketOtherSection,
  getBasketVehicles,
  updateReferenceQuantityRequest,
} from 'domains/basket/Basket.store';
import { useFetchSingleReference } from 'domains/catalog/Catalog.requests';
import {
  addReferenceFromCatalogToCart,
  getLastSearchedVehicleKey,
  getLastVehicleDetail,
  removeCartReferenceInCatalog,
} from 'domains/catalog/Catalog.store';
import { convertReferenceToEstimateReference } from 'domains/estimate/Estimate.mapper';
import { addCatalogReference } from 'domains/estimate/Estimate.store';
import {
  DHReferenceLocal,
  getDHReference,
  getIAMReference,
  getIsStockAvailable,
  getPrice,
  LocalTabType,
} from 'domains/references';
import { useFetchFullTradingData } from 'domains/references/References.requests';
import { getUserRights, SparePartsViewType } from 'domains/user';
import { getProductLink, hasPrice, PriceField } from 'pages/CatalogPage/DH/Product/ProductMenu/ProductMenuList';
import { BrandImage } from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/BrandImage';
import { BlackButton, Box, Flex, ImageWithLoader, Link, Text, ThumbsUpRoundButton } from 'UI';
import { getData, getTractionBatteryOrderStatus, hasData, resolveTractionBatteryFamilyCode } from 'utils';
import { SProductMenuTable } from './ProductMenuTable.styled';

interface ProductMenuTableRow {
  key: string;
  referenceNumber: string;
  isBestSupersession?: boolean;
}

export interface ProductMenuTableProps {
  referenceNumbers: string[];
  sparePartsView: SparePartsViewType;
  parentRef: DHReferenceLocal;
  tabType: LocalTabType;
  bestReplacingReference?: string;
}

const ProductMenuFetchTrigger: React.FC<{ referenceNumber: string }> = ({ referenceNumber }) => {
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  useFetchSingleReference(referenceNumber, null, vehicleKey);
  useFetchFullTradingData(referenceNumber, vehicleKey);
  return null;
};

const BrandCell: React.FC<{ referenceNumber: string }> = ({ referenceNumber }) => {
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const vehicleBrand = useSelector(getLastVehicleDetail)?.vehicleBrand ?? 'OTHER';
  const reference = useSelector((state: RootState) => getDHReference(state, { referenceNumber, vehicleKey }));
  const referenceIam = useSelector((state: RootState) =>
    getIAMReference(state, { referenceNumber, vehicleKey: undefined }),
  );

  if (!hasData(reference)) {
    return null;
  }

  const iamData = getData(referenceIam);
  if (iamData?.supplier && iamData.supplier !== 'MOTRIO') {
    return (
      <ImageWithLoader
        alt={'Brand logo from IAM'}
        imageUrl={iamData.brandImageUrl}
        imageHeight={36}
        imageWidth={36}
        cursor={'auto'}
      />
    );
  }
  const refData = getData(reference);
  return <BrandImage referenceBrand={refData?.brand} vehicleBrand={vehicleBrand} size={36} />;
};

const ReferenceCell: React.FC<{
  referenceNumber: string;
  parentRef: DHReferenceLocal;
  tabType: LocalTabType;
  isBestSupersession?: boolean;
}> = ({ referenceNumber, parentRef, tabType, isBestSupersession }) => {
  const { query } = useParams<{ query: string }>();
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const reference = useSelector((state: RootState) => getDHReference(state, { referenceNumber, vehicleKey }));
  const refData = getData(reference);

  if (!refData) {
    return null;
  }

  const kitReference =
    tabType === 'kit' ? parentRef?.kitReferences?.find((r) => r.referenceNumber === referenceNumber) : undefined;

  return (
    <Link to={getProductLink(query, referenceNumber)}>
      <Flex direction={'column'} gap={4}>
        <Flex gap={8} align={'center'}>
          <Text type={'section_bold'} cursor={'pointer'} disableGutter>
            {refData.name}
          </Text>
          {isBestSupersession && <ThumbsUpRoundButton />}
        </Flex>
        <Text type={'section'} cursor={'pointer'} disableGutter>
          {`Ref: ${referenceNumber}`}
        </Text>
        {kitReference && (
          <Text type={'light_12_black_65'} cursor={'pointer'} disableGutter>
            {`(${kitReference.quantity} units)`}
          </Text>
        )}
      </Flex>
    </Link>
  );
};

const StockCell: React.FC<{ referenceNumber: string }> = ({ referenceNumber }) => {
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  return <StockDisplay vehicleKey={vehicleKey} referenceNumber={referenceNumber} />;
};

const PriceCell: React.FC<{ referenceNumber: string; sparePartsView: SparePartsViewType }> = ({
  referenceNumber,
  sparePartsView,
}) => {
  const price = getData(useSelector((state: RootState) => getPrice(state, referenceNumber)));
  if (!price || !hasPrice(price, sparePartsView)) {
    return null;
  }
  return <PriceField price={price} sparePartsView={sparePartsView} referenceNumber={referenceNumber} />;
};

const ActionsCell: React.FC<{
  referenceNumber: string;
  parentRef: DHReferenceLocal;
  tabType: LocalTabType;
}> = ({ referenceNumber, parentRef, tabType }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const vehicle = useSelector(getLastVehicleDetail);
  const price = getData(useSelector((state: RootState) => getPrice(state, referenceNumber)));
  const reference = useSelector((state: RootState) => getDHReference(state, { referenceNumber, vehicleKey }));
  const referenceIam = useSelector((state: RootState) =>
    getIAMReference(state, { referenceNumber, vehicleKey: undefined }),
  );
  const parentReferenceIam = useSelector((state: RootState) =>
    getIAMReference(state, { referenceNumber: parentRef?.referenceNumber, vehicleKey: undefined }),
  );
  const availableInUserCountry = useSelector((state: RootState) => getIsStockAvailable(state, referenceNumber));
  const userRoles = useSelector(getUserRights);
  const resolvedUserRoles = Array.isArray(userRoles) ? userRoles : undefined;
  const basketVehicles = useSelector(getBasketVehicles);
  const basketOtherSection = useSelector(getBasketOtherSection);

  if (tabType === 'kit') {
    return null;
  }

  const refData = getData(reference);
  const iamData = getData(referenceIam);
  const familyCode = resolveTractionBatteryFamilyCode(
    refData?.familyCode,
    iamData?.commercialFamily,
    parentRef?.familyCode,
    getData(parentReferenceIam)?.commercialFamily,
  );
  const isApplicableToCurrentVehicle = refData?.isApplicableToCurrentVehicle ?? false;
  const { orderBlocked, orderBlockedMessage: orderBlockedMessageConfig } = getTractionBatteryOrderStatus({
    userRoles: resolvedUserRoles,
    vin: vehicle?.vin,
    isCompatible: isApplicableToCurrentVehicle,
    familyCode,
  });
  const orderBlockedMessage = orderBlockedMessageConfig
    ? t(orderBlockedMessageConfig.key, orderBlockedMessageConfig.defaultMessage)
    : undefined;

  const referencesOfCurrentVeh = basketVehicles.find((v) => v.vehicleDetail.vehicleKey === vehicleKey)?.references;
  const isApplicableOrInCurrVehCart =
    vehicleKey &&
    (refData?.isApplicableToCurrentVehicle ||
      referencesOfCurrentVeh?.some((ref) => ref.referenceNumber === referenceNumber));
  const basketReferences = isApplicableOrInCurrVehCart ? referencesOfCurrentVeh : basketOtherSection?.references;
  const isInBasket = basketReferences?.find((ref) => ref.referenceNumber === referenceNumber);
  const refQuantity = isInBasket?.quantity as number;

  const showEstimateButton = tabType !== 'linked_references';

  const handleAddToEstimate = (quantity?: number) => {
    if (price?.garageView?.vatExcludedPrice) {
      dispatch(
        addCatalogReference({
          reference: convertReferenceToEstimateReference(
            referenceNumber,
            refData?.name ?? '',
            price,
            isApplicableToCurrentVehicle,
            'DATAHUB',
            refData?.brand,
            undefined,
            undefined,
            undefined,
            undefined,
            [],
            familyCode,
            undefined,
            quantity,
          ),
        }),
      );
    }
  };

  const handleQuantityChange = (quantity: number | string) => {
    if (quantity === 0) {
      dispatch(
        removeCartReferenceInCatalog({
          basketReferenceType: isApplicableOrInCurrVehCart ? 'VEHICLE' : 'OTHER',
          vehicleKey: isApplicableOrInCurrVehCart ? vehicleKey : undefined,
          reference: { referenceNumber, sellerNumber: undefined },
          referenceType: refData?.type ?? 'STANDARD',
          origin: undefined,
          supplierCode: undefined,
          referenceSource: 'STANDARD',
        }),
      );
    } else {
      dispatch(
        updateReferenceQuantityRequest({
          basketReferenceType: isApplicableOrInCurrVehCart ? 'VEHICLE' : 'OTHER',
          vehicleKey: isApplicableOrInCurrVehCart ? vehicleKey : undefined,
          externalBasketId: undefined,
          referenceToUpdate: { referenceNumber, sellerNumber: undefined },
          newQuantity: quantity as number,
          referenceType: refData?.type ?? 'STANDARD',
          isBulkOilReference: isBulkCertasOil(isInBasket?.familyCode, isInBasket?.packaging),
        }),
      );
    }
  };

  const handleAddToCart = () => {
    if (price?.garageView?.vatExcludedPrice && price.garageView?.vatIncludedPrice) {
      if (vehicleKey && vehicle && refData?.isApplicableToCurrentVehicle) {
        dispatch(addReferenceFromCatalogToCart({ referenceNumber }));
      } else {
        dispatch(
          addOtherSectionReferenceRequest({
            reference: refData?.brand === 'IAM' && refData?.name === '' ? iamData : refData,
          }),
        );
      }
    }
  };

  if (orderBlocked) {
    return (
      <Flex direction={'row'} gap={16} align={'center'}>
        <ReferenceUnavailableForOrder enabled message={orderBlockedMessage} />
        {showEstimateButton && (
          <AddToEstimateButtonAndDialog
            handleAddToEstimateClick={handleAddToEstimate}
            isVehicleCatalog={!!vehicleKey}
            buttonComponent={'icon'}
            disabled={!price?.garageView?.vatExcludedPrice}
          />
        )}
      </Flex>
    );
  }

  if (isInBasket) {
    return (
      <Flex direction={'row'} gap={16} align={'center'}>
        <StopPropagation>
          <QuantityModule value={refQuantity} onChange={handleQuantityChange} showDelete />
        </StopPropagation>
        {showEstimateButton && (
          <AddToEstimateButtonAndDialog
            handleAddToEstimateClick={handleAddToEstimate}
            isVehicleCatalog={!!vehicleKey}
            buttonComponent={'icon'}
            disabled={!price?.garageView?.vatExcludedPrice}
          />
        )}
      </Flex>
    );
  }

  return (
    <Flex direction={'row'} gap={16} align={'center'}>
      <BlackButton
        onClick={handleAddToCart}
        disabled={
          !availableInUserCountry || !(price?.garageView?.vatExcludedPrice && price.garageView?.vatIncludedPrice)
        }
      >
        {t('cart.action.add_to_cart', 'Add to cart')}
      </BlackButton>
      {showEstimateButton && (
        <AddToEstimateButtonAndDialog
          handleAddToEstimateClick={handleAddToEstimate}
          isVehicleCatalog={!!vehicleKey}
          buttonComponent={'icon'}
          disabled={!price?.garageView?.vatExcludedPrice}
        />
      )}
    </Flex>
  );
};

export function ProductMenuTable({
  referenceNumbers,
  sparePartsView,
  parentRef,
  tabType,
  bestReplacingReference,
}: Readonly<ProductMenuTableProps>) {
  const { t } = useTranslation();

  const dataSource: ProductMenuTableRow[] = referenceNumbers.map((r) => ({
    key: r,
    referenceNumber: r,
    isBestSupersession: !!bestReplacingReference && r === bestReplacingReference,
  }));

  const columns: ColumnsType<ProductMenuTableRow> = [
    {
      title: t('catalog.product.menu.table.brand', 'Brand'),
      width: 100,
      render: (_, record) => (
        <>
          <ProductMenuFetchTrigger referenceNumber={record.referenceNumber} />
          <BrandCell referenceNumber={record.referenceNumber} />
        </>
      ),
    },
    {
      title: t('catalog.product.menu.table.reference', 'Reference'),
      render: (_, record) => (
        <ReferenceCell
          referenceNumber={record.referenceNumber}
          parentRef={parentRef}
          tabType={tabType}
          isBestSupersession={record.isBestSupersession}
        />
      ),
    },
    {
      title: t('catalog.product.menu.table.stock', 'Stock'),
      render: (_, record) => <StockCell referenceNumber={record.referenceNumber} />,
    },
    {
      title: t('catalog.product.menu.table.price', 'Price'),
      render: (_, record) => <PriceCell referenceNumber={record.referenceNumber} sparePartsView={sparePartsView} />,
    },
    {
      title: t('catalog.product.menu.table.action', 'Action'),
      render: (_, record) => (
        <Flex justify={'flex-end'}>
          <Box>
            <ActionsCell referenceNumber={record.referenceNumber} parentRef={parentRef} tabType={tabType} />
          </Box>
        </Flex>
      ),
    },
  ];

  return (
    <SProductMenuTable<ProductMenuTableRow>
      dataSource={dataSource}
      columns={columns}
      pagination={false}
      rowKey={'referenceNumber'}
      size={'middle'}
    />
  );
}
